import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, marketingApi } from '@/services/api';
import SetupGuidePanel from '@/components/settings/SetupGuidePanel';
import { ORDER_NOTIFY_SETUP_GUIDE } from '@/components/settings/setupGuides';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Lock,
  Loader2,
  Mail,
  MessageCircle,
  RotateCcw,
  Save,
  Settings2,
  Smartphone,
  XCircle,
} from 'lucide-react';

const EVENTS: Array<{
  key: string;
  label: string;
  tpl: string;
  eventKey: string;
  invoicePdfWhatsapp?: string;
  invoicePdfEmail?: string;
}> = [
  {
    key: 'notify_order_placed',
    label: 'Order placed',
    tpl: 'tpl_order_placed',
    eventKey: 'placed',
    invoicePdfWhatsapp: 'invoice_pdf_whatsapp_placed',
    invoicePdfEmail: 'invoice_pdf_email_placed',
  },
  {
    key: 'notify_order_accepted',
    label: 'Order accepted',
    tpl: 'tpl_order_accepted',
    eventKey: 'accepted',
    invoicePdfWhatsapp: 'invoice_pdf_whatsapp_accepted',
    invoicePdfEmail: 'invoice_pdf_email_accepted',
  },
  { key: 'notify_ready_collection', label: 'Ready for collection', tpl: 'tpl_ready_collection', eventKey: 'ready' },
  { key: 'notify_out_for_delivery', label: 'Out for delivery', tpl: 'tpl_out_for_delivery', eventKey: 'transit' },
  { key: 'notify_delivered', label: 'Delivered', tpl: 'tpl_delivered', eventKey: 'delivered' },
  { key: 'notify_collected', label: 'Collected', tpl: 'tpl_collected', eventKey: 'collected' },
  { key: 'notify_rejected', label: 'Rejected', tpl: 'tpl_rejected', eventKey: 'rejected' },
  { key: 'notify_cancelled', label: 'Cancelled', tpl: 'tpl_cancelled', eventKey: 'cancelled' },
];

const CHANNEL_OPTIONS = ['WhatsApp', 'SMS', 'Email'] as const;
const CHANNEL_ICONS = {
  WhatsApp: MessageCircle,
  SMS: Smartphone,
  Email: Mail,
} as const;
const CHANNEL_CONFIGURE: Record<(typeof CHANNEL_OPTIONS)[number], string> = {
  WhatsApp: 'whatsapp',
  SMS: 'sms',
  Email: 'email',
};
const ENTITLEMENT_FLAG: Record<(typeof CHANNEL_OPTIONS)[number], string> = {
  WhatsApp: 'allow_marketing_whatsapp',
  SMS: 'allow_marketing_sms',
  Email: 'allow_marketing_email',
};

type TokenDef = { token: string; label: string; aliases?: string[] };
type ChannelStatus = {
  channel: string;
  entitled: boolean;
  connected: boolean;
  can_enable: boolean;
  enabled: boolean;
  status: 'ready' | 'needs_config' | 'locked' | string;
  status_label: string;
};
type FormState = Record<string, string | number | boolean>;

function parseChannelOrder(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(String).filter((c) => CHANNEL_OPTIONS.includes(c as (typeof CHANNEL_OPTIONS)[number]));
  }
  return String(raw || '')
    .split(',')
    .map((c) => c.trim())
    .filter((c) => CHANNEL_OPTIONS.includes(c as (typeof CHANNEL_OPTIONS)[number]));
}

function goConfigureChannel(channel: string) {
  const id = CHANNEL_CONFIGURE[channel as (typeof CHANNEL_OPTIONS)[number]] || 'whatsapp';
  window.location.href = `/settings?tab=marketing&configure=${id}`;
}

function applySample(template: string, sample: Record<string, string>): string {
  let out = template || '';
  for (const [k, v] of Object.entries(sample || {})) {
    out = out.split(`{{${k}}}`).join(String(v ?? ''));
  }
  return out;
}

function insertAtCursor(
  el: HTMLTextAreaElement | null,
  value: string,
  insert: string,
  onChange: (next: string) => void
) {
  if (!el) {
    onChange(value + insert);
    return;
  }
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  const next = value.slice(0, start) + insert + value.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + insert.length;
    el.setSelectionRange(pos, pos);
  });
}

export default function OrderNotificationsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['marketingNotifySettings'],
    queryFn: async () => (await marketingApi.getNotificationSettings()).data.message,
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 60_000,
  });

  const { data: channelPayload } = useQuery({
    queryKey: ['marketingChannels'],
    queryFn: async () => (await marketingApi.listChannels()).data.message,
    staleTime: 30_000,
    retry: 1,
  });

  const [form, setForm] = useState<FormState>({});
  const [enabledChannels, setEnabledChannels] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [customMode, setCustomMode] = useState<Record<string, boolean>>({});
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  const tokens: TokenDef[] = data?.tokens || [];
  const defaults: Record<string, string> = data?.defaults || {};
  const sample: Record<string, string> = data?.sample || {};

  const profileEntitled = useMemo(() => {
    const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
    const map: Record<string, boolean> = {};
    for (const ch of CHANNEL_OPTIONS) {
      const flag = ENTITLEMENT_FLAG[ch];
      map[ch] = Boolean(profile?.[flag]) || features.includes(flag);
    }
    return map;
  }, [profile]);

  const connectedMap = useMemo(() => {
    const fromList = (channelPayload?.connected || {}) as Record<string, boolean>;
    const map: Record<string, boolean> = {};
    for (const ch of CHANNEL_OPTIONS) {
      map[ch] = Boolean(fromList[ch]);
    }
    return map;
  }, [channelPayload?.connected]);

  const channelStatus: ChannelStatus[] = useMemo(() => {
    const fromApi: ChannelStatus[] = data?.channel_status || [];
    const byChannel = new Map(fromApi.map((r) => [r.channel, r]));

    return CHANNEL_OPTIONS.map((ch) => {
      const apiRow = byChannel.get(ch);
      // Same entitlement source as Marketing hub (profile flags), with API as secondary
      const entitled = Boolean(profileEntitled[ch] || apiRow?.entitled);
      const connected = Boolean(
        connectedMap[ch] || apiRow?.connected || (entitled && apiRow?.status === 'ready')
      );
      let status: ChannelStatus['status'];
      let status_label: string;
      if (!entitled) {
        status = 'locked';
        status_label = 'Add-on required';
      } else if (!connected) {
        status = 'needs_config';
        status_label = 'Configure credentials';
      } else {
        status = 'ready';
        status_label = 'Connected — ready';
      }
      const can_enable = entitled && connected;
      return {
        channel: ch,
        entitled,
        connected,
        can_enable,
        enabled: enabledChannels.includes(ch) && can_enable,
        status,
        status_label,
      };
    });
  }, [data?.channel_status, enabledChannels, profileEntitled, connectedMap]);

  useEffect(() => {
    if (!data) return;
    const defs: Record<string, string> = data.defaults || {};
    const order = parseChannelOrder(data.channel_order?.length ? data.channel_order : data.channel_preference);
    const statusRows = CHANNEL_OPTIONS.map((ch) => {
      const apiRow = (data.channel_status || []).find((r: ChannelStatus) => r.channel === ch);
      const entitled = Boolean(profileEntitled[ch] || apiRow?.entitled);
      const connected = Boolean(connectedMap[ch] || apiRow?.connected);
      return { channel: ch, can_enable: entitled && connected };
    });
    const ready = new Set<string>(statusRows.filter((r) => r.can_enable).map((r) => r.channel));
    const nextEnabled = order.length
      ? order.filter((c) => ready.has(c))
      : statusRows.filter((r) => r.can_enable).map((r) => r.channel);
    setEnabledChannels(nextEnabled);

    const next: FormState = {};
    const custom: Record<string, boolean> = {};
    for (const ev of EVENTS) {
      next[ev.key] = Boolean(data[ev.key]);
      const saved = (data[ev.tpl] || '').trim();
      const def = (defs[ev.eventKey] || '').trim();
      next[ev.tpl] = saved || def;
      custom[ev.tpl] = Boolean(saved && saved !== def);
      if (ev.invoicePdfWhatsapp) next[ev.invoicePdfWhatsapp] = Boolean(data[ev.invoicePdfWhatsapp]);
      if (ev.invoicePdfEmail) next[ev.invoicePdfEmail] = Boolean(data[ev.invoicePdfEmail]);
    }
    setForm(next);
    setCustomMode(custom);
  }, [data, profileEntitled, connectedMap]);

  const toggleChannel = (channel: string, canEnable: boolean) => {
    if (!canEnable) return;
    setEnabledChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );
  };

  const saveMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      marketingApi.saveNotificationSettings(payload),
    onSuccess: () => {
      toast.success('Notification settings saved');
      qc.invalidateQueries({ queryKey: ['marketingNotifySettings'] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not save';
      toast.error(String(msg));
    },
  });

  const handleSave = () => {
    const order = enabledChannels.filter(Boolean);
    const anyReady = channelStatus.some((r) => r.can_enable);
    if (!order.length && anyReady) {
      toast.error('Enable at least one connected channel');
      return;
    }
    const payload: Record<string, unknown> = {
      ...form,
      channel_order: order,
      channel_preference: order.join(','),
    };
    for (const ev of EVENTS) {
      if (!customMode[ev.tpl]) {
        payload[ev.tpl] = '';
      }
    }
    saveMut.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Order notifications</h3>
        <p className="text-sm text-gray-500 mt-1">
          Enable one or more channels — each enabled channel receives the same status message. Use the
          professional default templates, or customise with merge fields.
        </p>
      </div>

      <SetupGuidePanel guide={ORDER_NOTIFY_SETUP_GUIDE} accent="teal" />

      {/* Multi-channel enable / disable */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Channels</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Add-on enabled → Configure credentials, then Enable. Without the add-on → Contact Support.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {channelStatus.map((row) => {
            const Icon = CHANNEL_ICONS[row.channel as keyof typeof CHANNEL_ICONS] || MessageCircle;
            const on = enabledChannels.includes(row.channel) && row.can_enable;
            const locked = row.status === 'locked';
            const needsConfig = row.status === 'needs_config';
            return (
              <div
                key={row.channel}
                className={`rounded-xl border p-3 flex flex-col gap-2 min-w-0 ${
                  on
                    ? 'border-emerald-300 bg-emerald-50/40'
                    : locked
                      ? 'border-amber-200 bg-amber-50/30'
                      : 'border-gray-200 bg-gray-50/50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        on ? 'bg-emerald-100' : 'bg-white border border-gray-200'
                      }`}
                    >
                      {locked ? (
                        <Lock className="w-4 h-4 text-amber-700" />
                      ) : (
                        <Icon className="w-4 h-4 text-gray-700" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{row.channel}</p>
                      <p className="text-[11px] text-gray-500 truncate">{row.status_label}</p>
                    </div>
                  </div>
                  {locked ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  ) : on ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">
                      <CheckCircle2 className="w-3 h-3" /> Enabled
                    </span>
                  ) : needsConfig ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded shrink-0">
                      Included
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
                      <XCircle className="w-3 h-3" /> Disabled
                    </span>
                  )}
                </div>

                {locked ? (
                  <>
                    <button
                      type="button"
                      disabled
                      className="w-full text-sm font-medium rounded-lg py-2 bg-gray-100 text-gray-400 cursor-not-allowed"
                    >
                      Unavailable
                    </button>
                    <Link
                      to={`/support?subject=${encodeURIComponent(`Enable Marketing ${row.channel}`)}`}
                      className="text-[11px] text-amber-800 hover:underline"
                    >
                      Contact Support for add-on
                    </Link>
                  </>
                ) : needsConfig ? (
                  <button
                    type="button"
                    onClick={() => goConfigureChannel(row.channel)}
                    className="w-full text-sm font-medium rounded-lg py-2 bg-teal-600 text-white hover:bg-teal-700 inline-flex items-center justify-center gap-1.5"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Configure
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleChannel(row.channel, row.can_enable)}
                      className={`w-full text-sm font-medium rounded-lg py-2 transition-colors ${
                        on
                          ? 'bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                      {on ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      onClick={() => goConfigureChannel(row.channel)}
                      className="text-[11px] text-teal-700 hover:underline inline-flex items-center gap-1 self-start"
                    >
                      <Settings2 className="w-3 h-3" /> Edit credentials
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {enabledChannels.length > 1 && (
          <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
            Multi-channel on: each status update will be sent on{' '}
            <strong>{enabledChannels.join(', ')}</strong> (where the customer has a matching
            mobile/email).
          </p>
        )}
      </div>

      {/* Events + templates */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-900">Which messages to send</h4>
        {EVENTS.map((ev) => {
          const on = Boolean(form[ev.key]);
          const isOpen = expanded === ev.key;
          const tplValue = String(form[ev.tpl] || '');
          const isCustom = Boolean(customMode[ev.tpl]);
          const previewSrc = isCustom ? tplValue : defaults[ev.eventKey] || tplValue;
          const preview = applySample(previewSrc, sample);

          return (
            <div
              key={ev.key}
              className={`rounded-xl border bg-white overflow-hidden ${
                on ? 'border-teal-200' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-900 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={on}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((f) => ({ ...f, [ev.key]: checked }));
                      if (checked) setExpanded(ev.key);
                    }}
                  />
                  <span className="truncate">{ev.label}</span>
                </label>
                {on && (
                  <button
                    type="button"
                    className="text-xs text-teal-700 hover:underline inline-flex items-center gap-1 shrink-0"
                    onClick={() => setExpanded(isOpen ? null : ev.key)}
                  >
                    {isOpen ? (
                      <>
                        Hide template <ChevronUp className="w-3.5 h-3.5" />
                      </>
                    ) : (
                      <>
                        Edit template <ChevronDown className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                )}
              </div>

              {on && isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                  {(ev.invoicePdfWhatsapp || ev.invoicePdfEmail) && (
                    <div className="rounded-lg border border-teal-100 bg-teal-50/50 px-3 py-2.5 space-y-2">
                      <p className="text-[11px] font-semibold text-teal-800 uppercase tracking-wide">
                        Attach invoice PDF
                      </p>
                      <p className="text-[11px] text-teal-700/80">
                        Sent together with the template message on the selected channel (customer must have
                        a valid mobile / email).
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                        {ev.invoicePdfWhatsapp && (
                          <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={Boolean(form[ev.invoicePdfWhatsapp])}
                              disabled={!connectedMap.WhatsApp}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  [ev.invoicePdfWhatsapp!]: e.target.checked,
                                }))
                              }
                            />
                            <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                            Send Invoice PDF on WhatsApp
                            {!connectedMap.WhatsApp && (
                              <span className="text-[10px] text-amber-700">(configure WhatsApp first)</span>
                            )}
                          </label>
                        )}
                        {ev.invoicePdfEmail && (
                          <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={Boolean(form[ev.invoicePdfEmail])}
                              disabled={!connectedMap.Email}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  [ev.invoicePdfEmail!]: e.target.checked,
                                }))
                              }
                            />
                            <Mail className="w-3.5 h-3.5 text-sky-600" />
                            Send Invoice PDF on Email
                            {!connectedMap.Email && (
                              <span className="text-[10px] text-amber-700">(configure Email first)</span>
                            )}
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-medium text-gray-600">Template</label>
                    <select
                      className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white"
                      value={isCustom ? 'custom' : 'default'}
                      onChange={(e) => {
                        const custom = e.target.value === 'custom';
                        setCustomMode((m) => ({ ...m, [ev.tpl]: custom }));
                        if (!custom) {
                          setForm((f) => ({
                            ...f,
                            [ev.tpl]: defaults[ev.eventKey] || '',
                          }));
                        }
                      }}
                    >
                      <option value="default">Professional default</option>
                      <option value="custom">Custom message</option>
                    </select>
                    {isCustom && (
                      <button
                        type="button"
                        className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                        onClick={() => {
                          setCustomMode((m) => ({ ...m, [ev.tpl]: false }));
                          setForm((f) => ({ ...f, [ev.tpl]: defaults[ev.eventKey] || '' }));
                        }}
                      >
                        <RotateCcw className="w-3 h-3" /> Reset to default
                      </button>
                    )}
                  </div>

                  {isCustom && (
                    <>
                      <div>
                        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                          Insert field
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {tokens.map((t) => (
                            <button
                              key={t.token}
                              type="button"
                              title={`Insert {{${t.token}}}`}
                              className="text-[11px] px-2 py-1 rounded-md border border-gray-200 bg-gray-50 hover:bg-teal-50 hover:border-teal-300 text-gray-700"
                              onClick={() =>
                                insertAtCursor(
                                  textareaRefs.current[ev.tpl],
                                  String(form[ev.tpl] || ''),
                                  `{{${t.token}}}`,
                                  (next) => setForm((f) => ({ ...f, [ev.tpl]: next }))
                                )
                              }
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea
                        ref={(el) => {
                          textareaRefs.current[ev.tpl] = el;
                        }}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[140px] font-mono"
                        value={tplValue}
                        onChange={(e) => {
                          setCustomMode((m) => ({ ...m, [ev.tpl]: true }));
                          setForm((f) => ({ ...f, [ev.tpl]: e.target.value }));
                        }}
                        placeholder="Write your message… click fields above to insert tokens"
                      />
                    </>
                  )}

                  <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Preview
                    </p>
                    <pre className="text-xs text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">
                      {preview || '—'}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="btn-primary inline-flex items-center gap-2 text-sm"
        disabled={saveMut.isPending}
        onClick={handleSave}
      >
        {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save notifications
      </button>
    </div>
  );
}
